let _ = t => t,
  _t,
  _t2;
import { useEffect, useMemo, memo } from 'react';
import { PrefetchBoardSearch } from '../../rewrite/board/components/PrefetchBoardSearch';
import AsyncOverlayContainer from '../../overlay/components/AsyncOverlayContainer';
import AsyncFilterBarWrapper from '../components/AsyncFilterBarWrapper';
import { DataWellComponent } from '../../dataWell/DataWellComponent';
import { useSelectedObjectTypeDef } from '../../crmObjects/hooks/useSelectedObjectTypeDef';
import { BOARD, MAP, REPORT } from '../../views/constants/PageType';
import { useCurrentPageType } from '../../views/hooks/useCurrentPageType';
import FeedbackSurveyLoader from './FeedbackSurveyLoader';
import { denormalizeTypeId } from '../../rewrite/utils/denormalizeTypeId';
import { setMostRecentlyUsedPageType } from '../utils/mostRecentlyUsedPageType';
import OnboardingPatternWrapper from '../../onboarding/OnboardingPatternWrapper';
import { useShowDataManagementOnboardingAlert } from '../../onboarding/hooks/useShowDataManagementOnboardingAlert';
import { ForbiddenPage } from '../../pipelinePermissions/components/ForbiddenPage';
import { useCurrentPipelineId } from '../../rewrite/pipelines/hooks/useCurrentPipelineId';
import { usePipelines } from '../../rewrite/pipelines/context/PipelinesContext';
import { PipelineEmptyPage } from '../../rewrite/pipelines/components/PipelineEmptyPage';
import { BulkActionsContextProvider } from 'customer-data-bulk-actions/contexts/bulkActionsContext';
import { useTrackIndexPageViewOnLoad } from './useTrackIndexPageViewOnLoad';
import { useDocumentTitle } from './useDocumentTitle';
import { useRestoreRouteInterject } from './useRestoreRouteInterject';
import { useTrackIndexPageViewDynamic } from './useTrackIndexPageViewDynamic';
import useMarkOnMount from 'crm-fe-perf/timing/hooks/useMarkOnMount';
import { IndexPageMounted } from 'crm-fe-perf/timing/marks/crm-index-ui/performanceMarks';
import { BannerComponent } from '../../banner/BannerComponent';
import { InvoiceSyncFailureBanner } from 'accounting-integrations-ui-components/components/InvoiceSyncFailureBanner';
import { tracker } from '../../tracking/usageTracker';
import { useCurrentViewId } from '../../views/hooks/useCurrentViewId';
import { useGetIsDataWellShown } from '../../dataWell/useIsDataWellShown';
import { useAfterSuccessActions } from '../../rewrite/init/hooks/useAfterSuccessActions';
import AsyncHeader from './AsyncHeader';
import { getAdditionalMetricsDimensions } from '../../lib/metrics/getAdditionalMetricsDimensions';
import AsyncReportPage from './AsyncReportPage';
import { useOnPreviewSidebarClose } from '../../rewrite/crmObjects/hooks/useOnPreviewSidebarClose';
import { useSyncObjectCRUDState } from '../../rewrite/crmObjects/hooks/useSyncObjectCRUDState';
import { Flex } from 'foundations-components/components/Flex';
import AsyncIndexPageOnboardingTours from '../../tours/AsyncIndexPageOnboardingTours';
import { IARedesignSwitch } from '../../auth/components/IARedesignSwitch';
import AsyncToolbar from './AsyncToolbar';
import { View } from 'foundations-components/components/View';
import styledWithTheme from 'foundations-theming/utils';
import { useIsUngatedForVizSettingsM1 } from '../../auth/hooks/useIsUngatedForVizSettingsM1';
import IndexPageInlineSidebar, { IndexPageInlineSidebarProvider, useIndexPageInlineSidebar, createSidebars } from './IndexPageInlineSidebar';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { VizRenderer } from 'crm-index-visualization-core';
import ErrorBoundary from '../../errorBoundary/ErrorBoundary';
import FullPageError from '../../errorBoundary/FullPageError';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Null() {
  return null;
}
const MainContentWrapper = ({
  children
}) => {
  const gatedContent = /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    alignItems: "stretch",
    minBlockSize: "0",
    paddingBlockStart: "300",
    paddingInline: "1100",
    children: children
  });
  const ungatedContent = /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    alignItems: "stretch",
    minBlockSize: "0",
    marginInline: "300",
    marginBlockEnd: "300",
    children: children
  });
  return /*#__PURE__*/_jsx(IARedesignSwitch, {
    gatedContent: gatedContent,
    ungatedContent: ungatedContent
  });
};

// QUARTZ_TODO: This is a temporary fix to ensure the page height is correct.
// UIApplication renders a div and it breaks their styles
const FullPageContainer = styledWithTheme.div(_t || (_t = _`
  height: calc(100vh - var(--global-nav-height)) !important;
${0}
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
`), ({
  isRedesignUngated,
  theme
}) => isRedesignUngated && `background-color: ${theme.color['fill-surface-recessed']};`);
export const INDEX_PAGE_INLINE_SIDEBAR_WIDTH = '474px'; // match min width of embedded filters

const MainContentContainer = styledWithTheme.div(_t2 || (_t2 = _`
  width: ${0};
  transition: width 0.15s cubic-bezier(0.8, 0, 1, 1);
  display: flex;
  flex-direction: column;
  block-size: 100%;
  overflow: hidden;
`), ({
  sidebarIsOpen
}) => !sidebarIsOpen ? '100%' : `calc(100% - ${INDEX_PAGE_INLINE_SIDEBAR_WIDTH})`);
const FilterBarOrToolbar = () => {
  return /*#__PURE__*/_jsx(IARedesignSwitch, {
    gatedContent: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      flexGrow: "0",
      flexShrink: "0",
      children: /*#__PURE__*/_jsx(AsyncFilterBarWrapper, {})
    }),
    ungatedContent: /*#__PURE__*/_jsx(View, {
      "data-test-id": "toolbar-wrapper",
      flexShrink: "0",
      marginBlockEnd: "300",
      minBlockSize: "40px",
      children: /*#__PURE__*/_jsx(AsyncToolbar, {})
    })
  });
};
const DataWellToolbar = ({
  showDataWell
}) => {
  //toolbar renders data well in the same container
  return /*#__PURE__*/_jsx(IARedesignSwitch, {
    gatedContent: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      flexGrow: "0",
      flexShrink: "1",
      children: showDataWell && /*#__PURE__*/_jsx(DataWellComponent, {})
    }),
    ungatedContent: null
  });
};
const MainVisualization = ({
  pageType
}) => {
  if (pageType === REPORT) {
    return /*#__PURE__*/_jsx(AsyncReportPage, {});
  }
  return /*#__PURE__*/_jsx(Flex, {
    flexGrow: pageType === BOARD || pageType === MAP ? '1' : '0',
    flexShrink: "1",
    flexBasis: "auto",
    overflow: "auto",
    flexDirection: "column",
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      ErrorComponent: FullPageError,
      boundaryName: "IndexPageVizRenderer",
      children: /*#__PURE__*/_jsx(VizRenderer, {
        LoadingComponent: LoadingSpinner,
        ErrorComponent: Null,
        pageType: pageType
      })
    })
  });
};
const ViewVizContainer = ({
  children
}) => {
  return /*#__PURE__*/_jsx(IARedesignSwitch, {
    gatedContent: /*#__PURE__*/_jsx(_Fragment, {
      children: children
    }),
    ungatedContent: /*#__PURE__*/_jsx(Flex, {
      paddingBlock: "300",
      paddingInline: "400",
      borderWidth: "100",
      borderColor: "border-core-subtle",
      borderStyle: "solid",
      borderRadius: "100",
      backgroundColor: "fill-surface-default",
      flexDirection: "column",
      blockSize: "100%",
      children: children
    })
  });
};

// Main layout component that uses the sidebar context
const MainLayout = /*#__PURE__*/memo(() => {
  const {
    activeSidebarType
  } = useIndexPageInlineSidebar();
  const pageType = useCurrentPageType();
  const isBoard = pageType === BOARD;
  const isUngatedForVizSettingsM1 = useIsUngatedForVizSettingsM1();
  const typeDef = useSelectedObjectTypeDef();
  const viewId = useCurrentViewId();
  const {
    data: isDataWellShownData
  } = useGetIsDataWellShown({
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    objectTypeId: typeDef.objectTypeId,
    viewId: viewId
  });
  const isDataWellShown = useMemo(() => {
    return isDataWellShownData === null || isDataWellShownData === void 0 ? void 0 : isDataWellShownData.isDataWellShown;
  }, [isDataWellShownData]);
  const showDataWell = pageType !== REPORT && isDataWellShown;
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "row",
    flexGrow: "1",
    flexShrink: "1",
    minBlockSize: "0",
    children: /*#__PURE__*/_jsxs(VizRenderer, {
      pageType: pageType,
      forProvider: true,
      LoadingComponent: LoadingSpinner,
      ErrorComponent: Null,
      children: [/*#__PURE__*/_jsx(AsyncOverlayContainer, {}), /*#__PURE__*/_jsx(MainContentContainer, {
        sidebarIsOpen: !!activeSidebarType,
        children: /*#__PURE__*/_jsxs(ViewVizContainer, {
          children: [isBoard && /*#__PURE__*/_jsx(PrefetchBoardSearch, {}), /*#__PURE__*/_jsx(FilterBarOrToolbar, {}), /*#__PURE__*/_jsx(DataWellToolbar, {
            showDataWell: !!showDataWell
          }), /*#__PURE__*/_jsx(MainVisualization, {
            pageType: pageType
          })]
        })
      }), isUngatedForVizSettingsM1 && /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        flexGrow: "1",
        children: /*#__PURE__*/_jsx(IndexPageInlineSidebar, {
          sidebars: createSidebars(pageType)
        })
      })]
    })
  });
});
MainLayout.displayName = 'MainLayout';
const IndexPage = () => {
  useOnPreviewSidebarClose();
  useSyncObjectCRUDState();
  const typeDef = useSelectedObjectTypeDef();
  const pageType = useCurrentPageType();
  useMarkOnMount(IndexPageMounted, getAdditionalMetricsDimensions(typeDef === null || typeDef === void 0 ? void 0 : typeDef.objectTypeId, pageType));
  useAfterSuccessActions();
  const currentPipelineId = useCurrentPipelineId();
  const pipelines = usePipelines();
  const isBoard = pageType === BOARD;
  const isEmptyBoard = isBoard && pipelines.length === 0;
  const shouldRenderPipelineLockScreen = isBoard && !isEmptyBoard && !currentPipelineId;
  useDocumentTitle();
  useRestoreRouteInterject();
  useShowDataManagementOnboardingAlert();
  useEffect(() => {
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    setMostRecentlyUsedPageType(typeDef.objectTypeId, pageType);
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
  }, [pageType, typeDef.objectTypeId]);

  // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
  const objectType = denormalizeTypeId(typeDef.objectTypeId);
  const renderPage = useMemo(() => {
    if (isEmptyBoard) {
      return /*#__PURE__*/_jsxs(MainContentWrapper, {
        children: [/*#__PURE__*/_jsx(FilterBarOrToolbar, {}), /*#__PURE__*/_jsx(PipelineEmptyPage, {})]
      });
    }
    if (shouldRenderPipelineLockScreen) {
      return /*#__PURE__*/_jsx(ForbiddenPage, {});
    }
    return /*#__PURE__*/_jsx(MainContentWrapper, {
      children: /*#__PURE__*/_jsx(MainLayout, {})
    });
  }, [isEmptyBoard, shouldRenderPipelineLockScreen]);

  // @ts-expect-error ts-migrate(2322) FIXME: Type 'undefined' is not assignable to type '{ obje... Remove this comment to see the full error message
  useTrackIndexPageViewOnLoad({
    pageType,
    typeDef
  });
  useTrackIndexPageViewDynamic();
  return (
    /*#__PURE__*/
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    _jsxs(BulkActionsContextProvider, {
      objectTypeId: typeDef.objectTypeId,
      children: [/*#__PURE__*/_jsx(OnboardingPatternWrapper, {
        objectType: objectType
      }), /*#__PURE__*/_jsx(FeedbackSurveyLoader, {}), /*#__PURE__*/_jsx(BannerComponent, {}), /*#__PURE__*/_jsx(InvoiceSyncFailureBanner, {
        objectType: objectType,
        usageTracker: tracker
      }), /*#__PURE__*/_jsx(IndexPageInlineSidebarProvider, {
        children: /*#__PURE__*/_jsx(IARedesignSwitch, {
          gatedContent: /*#__PURE__*/_jsxs(FullPageContainer, {
            className: "__hs__crm",
            "data-test-table-version": "v2",
            id: "crm",
            isRedesignUngated: false,
            children: [/*#__PURE__*/_jsx(AsyncHeader, {}), /*#__PURE__*/_jsx(AsyncIndexPageOnboardingTours, {}), renderPage]
          }),
          ungatedContent: /*#__PURE__*/_jsxs(FullPageContainer, {
            "data-test-table-version": "v2",
            id: "crm",
            "data-selenium-test": "crm-index-page",
            isRedesignUngated: true,
            children: [/*#__PURE__*/_jsx(AsyncHeader, {}), /*#__PURE__*/_jsx(AsyncIndexPageOnboardingTours, {}), renderPage]
          })
        })
      })]
    })
  );
};
export default IndexPage;